import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { EASE } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Empty, Field, PageHead, Panel, Skeleton, Switch, useToast } from '../components/ui.tsx';
import { ApiError, get, patch, post, put } from '../lib/api.ts';
import { useLookups, useMe } from '../lib/session.ts';

type UnitKind = 'department' | 'division' | 'section';
interface Unit {
  id: string;
  kind: UnitKind;
  name: string;
  code: string | null;
  headcount: number;
  archived: boolean;
}
interface Structure {
  units: Unit[];
  links: { parentKind: string; parentId: string; childId: string }[];
  /** Positions tied to each unit: what the people pickers offer there. */
  positions: { unitId: string; positionId: string }[];
}
type UnitEdit = { kind: UnitKind; unit: Unit | null; parent: Unit | null };

const PLURAL: Record<UnitKind, string> = { department: 'Departments', division: 'Divisions', section: 'Sections' };

export function Org() {
  const me = useMe().data!;
  const canEdit = me.access.orgEdit;
  const [showInactive, setShowInactive] = useState(false);
  const q = useQuery({ queryKey: ['org-structure', showInactive ? 'all' : 'active'], queryFn: () => get<Structure>(`/api/org/structure${showInactive ? '?archived=1' : ''}`) });
  const [dept, setDept] = useState<string | null>(null);
  const [div, setDiv] = useState<string | null>(null);
  const [sec, setSec] = useState<string | null>(null);
  const [editing, setEditing] = useState<UnitEdit | null>(null);
  const [tying, setTying] = useState<Unit | null>(null);
  const jobs = useQuery({ queryKey: ['org-jobs'], queryFn: () => get<{ positions: Job[]; levels: Job[] }>('/api/org/jobs') });

  const { departments, divisionsOf, sectionsOf, total } = useMemo(() => {
    const units = q.data?.units ?? [];
    const links = q.data?.links ?? [];
    const byId = new Map(units.map((u) => [u.id, u]));
    const children = (parentId: string) => links.filter((l) => l.parentId === parentId).map((l) => byId.get(l.childId)).filter((u): u is Unit => !!u);
    const rollup = (u: Unit): number => u.headcount + children(u.id).reduce((n, c) => n + rollup(c), 0);
    const deps = units.filter((u) => u.kind === 'department').map((u) => ({ ...u, total: rollup(u) }));
    return {
      departments: deps,
      divisionsOf: (id: string) => children(id).map((u) => ({ ...u, total: rollup(u) })),
      sectionsOf: (id: string) => children(id).map((u) => ({ ...u, total: rollup(u) })),
      total: deps.reduce((n, d) => n + d.total, 0),
    };
  }, [q.data]);

  const activeDept = departments.some((d) => d.id === dept) ? dept : (departments[0]?.id ?? null);
  const divisions = activeDept ? divisionsOf(activeDept) : [];
  const activeDiv = div && divisions.some((d) => d.id === div) ? div : (divisions[0]?.id ?? null);
  const sections = activeDiv ? sectionsOf(activeDiv) : [];
  const activeSec = sec && sections.some((x) => x.id === sec) ? sec : null;
  const unitById = (id: string | null) => q.data?.units.find((u) => u.id === id) ?? null;
  // The section if one is picked, else the division: its positions are what the people pickers offer there.
  const tieUnit = unitById(activeSec ?? activeDiv);
  const tiedIds = new Set((q.data?.positions ?? []).filter((t) => t.unitId === tieUnit?.id).map((t) => t.positionId));
  const tied = (jobs.data?.positions ?? []).filter((j) => tiedIds.has(j.id));
  const fallsBack = !tied.length && tieUnit?.kind === 'section' && (q.data?.positions ?? []).some((t) => t.unitId === activeDiv);

  const Column = ({ kind, items, active, onPick, empty, parent }: { kind: UnitKind; items: (Unit & { total: number })[]; active: string | null; onPick: (id: string) => void; empty: string; parent: Unit | null }) => (
    <div className="stack" style={{ ['--gap' as string]: '10px', alignContent: 'start' }}>
      <div className="row row--between">
        <span className="eyebrow">
          {PLURAL[kind]} <span style={{ color: 'var(--ink-4)' }}>· {items.filter((u) => !u.archived).length}</span>
        </span>
        {canEdit && (kind === 'department' || parent) ? (
          <Button size="sm" variant="ghost" icon="plus" onClick={() => setEditing({ kind, unit: null, parent })} title={parent ? `Add a ${kind} under ${parent.name}` : undefined}>
            Add
          </Button>
        ) : null}
      </div>
      <AnimatePresence mode="popLayout">
        {items.length ? (
          items.map((u, i) => (
            <motion.div key={u.id} className="org-node-wrap" style={{ opacity: u.archived ? 0.55 : 1 }} initial={{ opacity: 0, x: -8 }} animate={{ opacity: u.archived ? 0.55 : 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.45, ease: EASE, delay: i * 0.035 }} layout>
              <button type="button" className="org-node" aria-pressed={u.id === active} onClick={() => onPick(u.id)}>
                <span className="org-node__name">{u.name}</span>
                <span className="display num" style={{ fontSize: '1.3rem', color: u.id === active ? 'var(--accent-text)' : 'var(--ink-2)' }}>
                  {u.total}
                </span>
                <span className="eyebrow" style={{ gridColumn: '1 / -1' }}>
                  {u.archived ? 'inactive' : (u.code ?? u.kind)}
                </span>
              </button>
              {canEdit ? (
                <button type="button" className="org-node__edit" aria-label={`Edit ${u.name}`} title="Edit" onClick={() => setEditing({ kind, unit: u, parent })}>
                  Edit
                </button>
              ) : null}
            </motion.div>
          ))
        ) : (
          <p className="faint" style={{ fontSize: 'var(--step--1)' }}>
            {empty}
          </p>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div>
      <PageHead
        eyebrow={`Organisation · ${total} people`}
        title={
          <>
            How the company <em>fits together.</em>
          </>
        }
        lede="Departments contain divisions; divisions contain sections. A unit can sit under more than one parent, exactly as it did in the old system."
        aside={canEdit ? <Switch checked={showInactive} onChange={setShowInactive} label="Show inactive units" showLabel /> : null}
      />
      {q.isLoading ? (
        <Skeleton h={320} />
      ) : !departments.length ? (
        <Empty title="No structure yet" />
      ) : (
        <div className="org-columns">
          <Column kind="department" items={departments} active={activeDept} onPick={(id) => { setDept(id); setDiv(null); }} empty="" parent={null} />
          <Column kind="division" items={divisions} active={activeDiv} onPick={(id) => { setDiv(id); setSec(null); }} empty="No divisions under this department." parent={unitById(activeDept)} />
          <Column kind="section" items={sections} active={activeSec} onPick={(id) => setSec(activeSec === id ? null : id)} empty="No sections — people report to the division directly." parent={unitById(activeDiv)} />
        </div>
      )}
      {tieUnit ? (
        <div style={{ marginTop: 'var(--s-5)' }}>
          <Panel
            eyebrow={`Positions · ${tieUnit.kind} ${tieUnit.name}`}
            title={tied.length ? `${tied.length} position${tied.length === 1 ? '' : 's'}` : 'No positions tied yet'}
            action={canEdit ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setTying(tieUnit)}>Choose positions</Button> : undefined}
          >
            {tied.length ? (
              <div className="row row--wrap" style={{ gap: 6 }}>
                {tied.map((j) => (
                  <Chip key={j.id}>{j.name}{j.archived ? ' (inactive)' : ''}</Chip>
                ))}
              </div>
            ) : null}
            <p className="faint" style={{ fontSize: 'var(--step--1)', marginTop: tied.length ? 'var(--s-3)' : 0 }}>
              {tied.length
                ? `Recruit and Edit employment offer only these positions for people placed in ${tieUnit.name}.`
                : fallsBack
                  ? `People placed in ${tieUnit.name} are offered the division’s positions until some are tied here.`
                  : `Until some are tied, people placed here are offered every position.`}
              {tieUnit.kind === 'division' ? ' Pick a section to see its own.' : ''}
            </p>
          </Panel>
        </div>
      ) : null}
      {tying ? <PositionsDialog unit={tying} all={jobs.data?.positions ?? []} tied={new Set((q.data?.positions ?? []).filter((t) => t.unitId === tying.id).map((t) => t.positionId))} onClose={() => setTying(null)} /> : null}
      {canEdit && !q.isLoading && !departments.length ? (
        <div className="row" style={{ marginTop: 'var(--s-4)' }}>
          <Button icon="plus" onClick={() => setEditing({ kind: 'department', unit: null, parent: null })}>Add a department</Button>
        </div>
      ) : null}
      {editing ? <UnitDialog edit={editing} links={q.data?.links ?? []} units={q.data?.units ?? []} onClose={() => setEditing(null)} onSaved={(u) => (u.kind === 'department' ? setDept(u.id) : u.kind === 'division' ? setDiv(u.id) : setSec(u.id))} /> : null}
      <Jobs />
      <p className="faint" style={{ marginTop: 'var(--s-6)', fontSize: 'var(--step--1)' }}>
        Looking for someone? <Link to="/people" className="link">Open the directory</Link>.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- Departments, divisions, sections

/** Add a unit under its parent, or rename, re-code or deactivate one. A department also says which offices it serves; a section, which divisions it belongs to. */
function UnitDialog({ edit, links, units, onClose, onSaved }: { edit: UnitEdit; links: Structure['links']; units: Unit[]; onClose: () => void; onSaved: (u: { id: string; kind: UnitKind }) => void }) {
  const { kind, unit, parent } = edit;
  const qc = useQueryClient();
  const toast = useToast();
  const lookups = useLookups();
  const me = useMe().data!;
  const officesNow = unit ? links.filter((l) => l.childId === unit.id && l.parentKind === 'office').map((l) => l.parentId) : me.employee?.officeId ? [me.employee.officeId] : [];
  const divisionsNow = unit ? links.filter((l) => l.childId === unit.id && l.parentKind === 'division').map((l) => l.parentId) : [];
  const [name, setName] = useState(unit?.name ?? '');
  const [code, setCode] = useState(unit?.code ?? '');
  const [offices, setOffices] = useState<string[]>(officesNow);
  const [divisions, setDivisions] = useState<string[]>(divisionsNow);
  const allDivisions = units.filter((u) => u.kind === 'division' && (!u.archived || divisionsNow.includes(u.id)));
  const done = (title: string, saved?: { id: string; kind: UnitKind }) => {
    toast({ title });
    void qc.invalidateQueries({ queryKey: ['org-structure'] });
    if (saved) onSaved(saved);
    onClose();
  };
  const body = { name: name.trim(), code: code.trim() || (unit ? null : undefined), ...(kind === 'department' ? { offices } : {}), ...(kind === 'section' && unit ? { divisions } : {}) };
  const save = useMutation({
    mutationFn: () =>
      unit
        ? patch<{ id: string; kind: UnitKind }>(`/api/org/units/${unit.id}`, body)
        : post<{ id: string; kind: UnitKind }>('/api/org/units', { kind, name: body.name, code: body.code, parents: kind === 'department' ? offices.map((id) => ({ kind: 'office', id })) : [{ kind: parent!.kind, id: parent!.id }] }),
    onSuccess: (u) => done(unit ? `${body.name} saved` : `${body.name} added`, unit ? undefined : u),
  });
  const toggle = useMutation({ mutationFn: () => patch(`/api/org/units/${unit!.id}`, { archived: !unit!.archived }), onSuccess: () => done(unit!.archived ? `${unit!.name} is active again` : `${unit!.name} deactivated`) });
  const error = save.error ?? toggle.error;
  const ready = name.trim().length >= 2 && (kind !== 'department' || offices.length > 0) && (kind !== 'section' || !unit || divisions.length > 0);
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={unit ? kind[0]!.toUpperCase() + kind.slice(1) : parent ? `New ${kind} under ${parent.name}` : `New ${kind}`}
      title={unit ? unit.name : `Add a ${kind}`}
      width={520}
      footer={
        <>
          {unit ? (
            <Button variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate()}>
              {unit.archived ? 'Reactivate' : 'Deactivate'}
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>{unit ? 'Save' : 'Add'}</Button>
        </>
      }
    >
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-8">
          <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        </div>
        <div className="col-4">
          <Field label="Code" hint="Optional">{(p) => <input {...p} className="input input--mono" value={code} maxLength={20} onChange={(e) => setCode(e.target.value)} />}</Field>
        </div>
        {kind === 'department' ? (
          <div className="col-12">
            <Field label="Offices" hint="People pickers offer this department for these offices.">
              {(p) => (
                <div {...p} className="stack" style={{ ['--gap' as string]: '6px', maxHeight: 220, overflow: 'auto' }}>
                  {lookups.data?.offices.map((o) => (
                    <label key={o.id} className="row" style={{ ['--gap' as string]: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={offices.includes(o.id)} onChange={(e) => setOffices((cur) => (e.target.checked ? [...cur, o.id] : cur.filter((x) => x !== o.id)))} />
                      <span>{o.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </Field>
          </div>
        ) : null}
        {kind === 'section' && unit ? (
          <div className="col-12">
            <Field label="Divisions" hint={divisions.length ? 'The section is offered under these divisions.' : 'A section belongs to at least one division.'}>
              {(p) => (
                <div {...p} className="stack" style={{ ['--gap' as string]: '6px', maxHeight: 220, overflow: 'auto' }}>
                  {allDivisions.map((d) => (
                    <label key={d.id} className="row" style={{ ['--gap' as string]: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={divisions.includes(d.id)} onChange={(e) => setDivisions((cur) => (e.target.checked ? [...cur, d.id] : cur.filter((x) => x !== d.id)))} />
                      <span>{d.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </Field>
          </div>
        ) : null}
        {unit && unit.headcount ? (
          <div className="col-12">
            <p className="faint" style={{ fontSize: 'var(--step--1)' }}>
              {unit.headcount} {unit.headcount === 1 ? 'person is' : 'people are'} placed directly in this {kind}. A new name shows on their records at once; to deactivate it, move them first.
            </p>
          </div>
        ) : null}
        {error ? <div className="col-12"><Callout tone="bad">{errText(error)}</Callout></div> : null}
      </div>
    </Dialog>
  );
}

/** Which positions a division or section offers. Untying one leaves people who hold it as they are. */
function PositionsDialog({ unit, all, tied, onClose }: { unit: Unit; all: Job[]; tied: Set<string>; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [chosen, setChosen] = useState<Set<string>>(tied);
  const [filter, setFilter] = useState('');
  const shown = all.filter((j) => (!j.archived || tied.has(j.id)) && j.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const save = useMutation({
    mutationFn: () => put(`/api/org/units/${unit.id}/positions`, { positionIds: [...chosen] }),
    onSuccess: () => {
      toast({ title: `Positions of ${unit.name} saved` });
      void qc.invalidateQueries({ queryKey: ['org-structure'] });
      onClose();
    },
  });
  const toggle = (id: string, on: boolean) => setChosen((cur) => {
    const next = new Set(cur);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  });
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={`Positions · ${unit.kind}`}
      title={unit.name}
      width={560}
      footer={
        <>
          <span className="faint" style={{ flex: 1, fontSize: 'var(--step--1)' }}>{chosen.size} chosen</span>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted" style={{ fontSize: 'var(--step--1)' }}>Recruit and Edit employment offer only these positions for people placed in this {unit.kind}. Someone who already holds a position you untie keeps it.</p>
        <input className="input" placeholder="Filter positions…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter positions" />
        <div className="stack" style={{ ['--gap' as string]: '6px', maxHeight: 320, overflow: 'auto' }}>
          {shown.map((j) => (
            <label key={j.id} className="row" style={{ ['--gap' as string]: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={chosen.has(j.id)} onChange={(e) => toggle(j.id, e.target.checked)} />
              <span>{j.name}{j.archived ? <span className="faint"> (inactive)</span> : null}</span>
            </label>
          ))}
          {!shown.length ? <p className="faint">No position matches.</p> : null}
        </div>
        {save.error ? <Callout tone="bad">{errText(save.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Job positions & levels

interface Job {
  id: string;
  name: string;
  rank?: number;
  archived: boolean;
  holders: number;
}
type JobKind = 'position' | 'level';

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

function Jobs() {
  const me = useMe().data!;
  const canEdit = me.access.orgEdit;
  const q = useQuery({ queryKey: ['org-jobs'], queryFn: () => get<{ positions: Job[]; levels: Job[] }>('/api/org/jobs') });
  const [showInactive, setShowInactive] = useState(false);
  const [edit, setEdit] = useState<{ kind: JobKind; job: Job | null } | null>(null);

  const List = ({ kind, items }: { kind: JobKind; items: Job[] }) => {
    const shown = items.filter((j) => showInactive || !j.archived);
    return (
      <Panel
        eyebrow={`${items.filter((j) => !j.archived).length} active`}
        title={kind === 'position' ? 'Job positions' : 'Job levels'}
        action={canEdit ? <Button size="sm" icon="plus" onClick={() => setEdit({ kind, job: null })}>{kind === 'position' ? 'Position' : 'Level'}</Button> : undefined}
        flush
      >
        {shown.length ? (
          <table className="table">
            <thead>
              <tr>
                {kind === 'level' ? <th style={{ width: 56 }}>Rank</th> : null}
                <th>Name</th>
                <th className="right">People</th>
                {canEdit ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {shown.map((j) => (
                <tr key={j.id} style={{ opacity: j.archived ? 0.55 : 1 }}>
                  {kind === 'level' ? <td className="mono num">{j.rank}</td> : null}
                  <td>
                    {j.name} {j.archived ? <Chip>Inactive</Chip> : null}
                  </td>
                  <td className="right num">{j.holders || <span className="faint">—</span>}</td>
                  {canEdit ? (
                    <td className="right">
                      <Button size="sm" variant="ghost" onClick={() => setEdit({ kind, job: j })}>Edit</Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="faint" style={{ padding: 'var(--s-4)', fontSize: 'var(--step--1)' }}>None yet.</p>
        )}
      </Panel>
    );
  };

  return (
    <section style={{ marginTop: 'var(--s-7)' }}>
      <div className="row row--between" style={{ marginBottom: 'var(--s-4)' }}>
        <div className="stack" style={{ ['--gap' as string]: '6px' }}>
          <span className="eyebrow">Jobs</span>
          <p className="muted" style={{ fontSize: 'var(--step--1)', maxWidth: '58ch' }}>
            Every hire needs a position and a level. Inactive ones drop out of the pickers but stay on past records.
          </p>
        </div>
        <Switch checked={showInactive} onChange={setShowInactive} label="Show inactive" showLabel />
      </div>
      {q.isLoading ? (
        <Skeleton h={240} />
      ) : (
        <div className="grid-12" style={{ gap: 'var(--s-5)', alignItems: 'start' }}>
          <div className="col-7"><List kind="position" items={q.data?.positions ?? []} /></div>
          <div className="col-5"><List kind="level" items={q.data?.levels ?? []} /></div>
        </div>
      )}
      {edit ? <JobDialog kind={edit.kind} job={edit.job} levels={q.data?.levels ?? []} onClose={() => setEdit(null)} /> : null}
    </section>
  );
}

function JobDialog({ kind, job, levels, onClose }: { kind: JobKind; job: Job | null; levels: Job[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(job?.name ?? '');
  const [rank, setRank] = useState(String(job?.rank ?? Math.max(0, ...levels.map((l) => l.rank ?? 0)) + 1));
  const path = kind === 'position' ? '/api/org/positions' : '/api/org/levels';
  const what = kind === 'position' ? 'position' : 'level';
  const done = (title: string) => {
    toast({ title });
    void qc.invalidateQueries({ queryKey: ['org-jobs'] });
    void qc.invalidateQueries({ queryKey: ['lookups'] });
    onClose();
  };
  const body = { name: name.trim(), ...(kind === 'level' ? { rank: Number(rank) } : {}) };
  const save = useMutation({ mutationFn: () => (job ? patch(`${path}/${job.id}`, body) : post(path, body)), onSuccess: () => done(job ? `${body.name} saved` : `${body.name} added`) });
  const toggle = useMutation({ mutationFn: () => patch(`${path}/${job!.id}`, { archived: !job!.archived }), onSuccess: () => done(job!.archived ? `${job!.name} is active again` : `${job!.name} deactivated`) });
  const error = save.error ?? toggle.error;
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={job ? `Job ${what}` : `New job ${what}`}
      title={job ? job.name : `Add a ${what}`}
      width={480}
      footer={
        <>
          {job ? (
            <Button variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate()}>
              {job.archived ? 'Reactivate' : 'Deactivate'}
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{job ? 'Save' : 'Add'}</Button>
        </>
      }
    >
      <div className="grid-12" style={{ gap: 14 }}>
        <div className={kind === 'level' ? 'col-8' : 'col-12'}>
          <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        </div>
        {kind === 'level' ? (
          <div className="col-4">
            <Field label="Rank" hint="1 is the most junior">{(p) => <input {...p} type="number" min={1} max={99} className="input input--mono" value={rank} onChange={(e) => setRank(e.target.value)} />}</Field>
          </div>
        ) : null}
        {job && job.holders ? (
          <div className="col-12">
            <p className="faint" style={{ fontSize: 'var(--step--1)' }}>
              {job.holders} {job.holders === 1 ? 'person holds' : 'people hold'} this {what}. A new name shows on their records at once.
            </p>
          </div>
        ) : null}
        {error ? <div className="col-12"><Callout tone="bad">{errText(error)}</Callout></div> : null}
      </div>
    </Dialog>
  );
}
